import React, { useMemo, useRef, useState } from 'react';
import { TextInput, UnstyledButton } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClock, faMagnifyingGlass, faRightFromBracket, faRightToBracket, faRotateRight, faXmark } from '@fortawesome/free-solid-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { AttendanceActionType } from 'shared-models/browser';
import type { EmployeeView } from '../../../../services/employees/employees.service';
import { PickListModal } from '../../../../components/PickListModal/PickListModal';
import { PinPadModal } from './PinPadModal';
import { ChefHatIcon } from '../settings/ChefHatIcon';
import { sortEmployeesForPicker } from './employee-sort.utils';
import classes from './employee-identification-gate.module.scss';

const PIN_LENGTH = 4;

const formatDurationHmFromMinutes = (minutes: number): string => {
    const totalMinutes = Math.max(0, Math.floor(minutes));
    const hours = Math.floor(totalMinutes / 60);
    const mins = totalMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
};

type EmployeeIdentificationGateProps = {
    employees: EmployeeView[];
    isLoadingEmployees: boolean;
    isIdentifying: boolean;
    error: string | null;
    tenantPhotoUrl?: string | null;
    onIdentify: (employeeId: number, pin: string) => Promise<boolean>;
    onAttendanceAction?: (employeeId: number, pin: string, actionType: AttendanceActionType) => Promise<boolean>;
    onResolveAttendanceShiftStatus?: (employeeId: number) => Promise<{
        isOpenShift: boolean;
        currentDurationMinutes: number;
    }>;
    attendanceUiEnabled?: boolean;
    isAttendanceActioning?: boolean;
    onRefresh: () => void | Promise<void>;
    onClearError: () => void;
    onBeforeSelectEmployee?: (employee: EmployeeView) => Promise<boolean>;
    onOpenKds?: () => void;
};

// Architectural summary:
// Hebrew RTL employee picker that opens the reusable `PinPadModal` once the
// operator selects an employee. The picker stays mounted underneath the
// modal so closing the modal returns the operator to the same scrolled
// position with no flash.
// Touches:
//   - PinPadModal (PIN entry UX, auto-submit, error reset).
//   - employees.service.ts (via `onIdentify` in PosShellPage.handleIdentify).
// Breaking change risk: Removing the modal in favour of a separate route
// loses the "stays in context" behaviour the design relies on.
export const EmployeeIdentificationGate: React.FC<EmployeeIdentificationGateProps> = ({
    employees,
    isLoadingEmployees,
    isIdentifying,
    error: _error,
    tenantPhotoUrl,
    onIdentify,
    onAttendanceAction,
    onResolveAttendanceShiftStatus,
    attendanceUiEnabled = true,
    isAttendanceActioning = false,
    onRefresh,
    onClearError,
    onBeforeSelectEmployee,
    onOpenKds,
}) => {
    const [search, setSearch] = useState('');
    const [selectedEmployee, setSelectedEmployee] = useState<EmployeeView | null>(null);
    const [isAttendanceActionsModalOpen, setIsAttendanceActionsModalOpen] = useState(false);
    const [attendanceActionType, setAttendanceActionType] = useState<AttendanceActionType | null>(null);
    const [attendanceEmployee, setAttendanceEmployee] = useState<EmployeeView | null>(null);
    const [attendancePinOpen, setAttendancePinOpen] = useState(false);
    const [attendanceShiftDurationMinutes, setAttendanceShiftDurationMinutes] = useState<number | null>(null);
    const [isResolvingAttendanceStatus, setIsResolvingAttendanceStatus] = useState(false);
    const [failedPhotoUrls, setFailedPhotoUrls] = useState<Set<string>>(() => new Set());
    const isPhotoRefreshInFlight = useRef(false);

    // Architectural summary: Sorts (Latin, Hebrew, digits/symbols) then filters by fullName.
    const filteredEmployees = useMemo(() => {
        const sorted = sortEmployeesForPicker(employees);
        const normalized = search.trim().toLowerCase();
        if (!normalized) {
            return sorted;
        }
        return sorted.filter((employee) =>
            employee.fullName.toLowerCase().includes(normalized),
        );
    }, [employees, search]);
    const sortedEmployees = useMemo(() => sortEmployeesForPicker(employees), [employees]);

    const initialOf = (name: string) => name.trim().charAt(0).toUpperCase() || '?';

    // Signed S3 URLs expire while a POS remains open. Fall back to initials for the failed URL,
    // then fetch the employee list once so every visible employee receives a fresh signed URL.
    const handlePhotoError = (photoUrl: string) => {
        setFailedPhotoUrls((previous) => new Set(previous).add(photoUrl));
        if (isPhotoRefreshInFlight.current) {
            return;
        }
        isPhotoRefreshInFlight.current = true;
        void Promise.resolve(onRefresh()).finally(() => {
            isPhotoRefreshInFlight.current = false;
        });
    };

    // Architectural summary: Closes the PIN modal and clears any prior error so the next attempt starts clean.
    const closePinModal = () => {
        setSelectedEmployee(null);
        onClearError();
    };

    const closeAttendanceFlow = () => {
        setIsAttendanceActionsModalOpen(false);
        setAttendancePinOpen(false);
        setAttendanceActionType(null);
        setAttendanceEmployee(null);
        setAttendanceShiftDurationMinutes(null);
        setIsResolvingAttendanceStatus(false);
    };

    // Architectural summary: Submits the entered PIN to the parent identifier; returns true on success so PinPadModal does not clear the PIN buffer (parent will unmount the gate on success anyway).
    const submitPin = async (pin: string): Promise<boolean> => {
        if (!selectedEmployee) {
            return false;
        }
        const ok = await onIdentify(selectedEmployee.id, pin);
        if (!ok) {
            setSelectedEmployee(null);
        }
        return ok;
    };

    const submitAttendancePin = async (pin: string): Promise<boolean> => {
        if (!attendanceEmployee || !attendanceActionType || !onAttendanceAction) {
            return false;
        }
        const ok = await onAttendanceAction(attendanceEmployee.id, pin, attendanceActionType);
        if (ok) {
            closeAttendanceFlow();
        }
        return ok;
    };

    const openAttendanceActions = () => {
        onClearError();
        setAttendanceEmployee(null);
        setAttendanceActionType(null);
        setAttendancePinOpen(false);
        setAttendanceShiftDurationMinutes(null);
        setIsAttendanceActionsModalOpen(true);
    };

    const showAttendanceTrigger = attendanceUiEnabled && onAttendanceAction;

    return (
        <div className={classes.container} data-testid="employee-identification-gate">
            <div className={classes.gateShell}>
                {(onOpenKds || showAttendanceTrigger) && (
                    <div className={classes.topActions}>
                        {showAttendanceTrigger && (
                            <UnstyledButton
                                type="button"
                                data-testid="attendance-actions-open"
                                className={classes.attendanceTriggerButton}
                                onClick={openAttendanceActions}
                                disabled={isAttendanceActioning || isLoadingEmployees}
                                aria-label="כניסה למשמרת"
                            >
                                <FontAwesomeIcon icon={faClock as IconProp} />
                                <span className={classes.attendanceTriggerLabel}>כניסה למשמרת</span>
                            </UnstyledButton>
                        )}

                        {onOpenKds && (
                            <UnstyledButton
                                type="button"
                                data-testid="employee-identification-open-kds"
                                className={classes.kdsButton}
                                onClick={onOpenKds}
                                aria-label="מסך מטבח"
                            >
                                <ChefHatIcon className={classes.kdsButtonIcon} />
                                <span>מסך מטבח</span>
                            </UnstyledButton>
                        )}
                    </div>
                )}

                <div className={classes.card}>
                    {tenantPhotoUrl && (
                        <div className={classes.tenantLogo}>
                            <img src={tenantPhotoUrl} alt="לוגו החברה" data-testid="employee-identification-tenant-logo" />
                        </div>
                    )}
                    <div className={classes.header}>
                        <h2 className={classes.headerTitle}>בחר עובד להתחברות</h2>
                    </div>

                    <div className={classes.searchRow}>
                        <div className={classes.search}>
                            <TextInput
                                data-testid="employee-identification-search"
                                value={search}
                                onChange={(event) => setSearch(event.currentTarget.value)}
                                placeholder="חיפוש עובד..."
                                size="md"
                                radius="md"
                                leftSection={<FontAwesomeIcon icon={faMagnifyingGlass as IconProp} />}
                            />
                        </div>
                        <UnstyledButton
                            type="button"
                            data-testid="employee-identification-refresh"
                            className={classes.refreshButton}
                            onClick={onRefresh}
                            disabled={isLoadingEmployees}
                            aria-label="רענן"
                        >
                            <FontAwesomeIcon icon={faRotateRight as IconProp} />
                        </UnstyledButton>
                    </div>

                    <div className={classes.list}>
                        {filteredEmployees.map((employee, index) => {
                            const colored = index % 4 === 2;
                            return (
                                <UnstyledButton
                                    key={employee.id}
                                    type="button"
                                    data-testid={`employee-identification-row-${employee.id}`}
                                    className={classes.row}
                                    onClick={async () => {
                                        onClearError();
                                        if (onBeforeSelectEmployee) {
                                            const proceed = await onBeforeSelectEmployee(employee);
                                            if (!proceed) return;
                                        }
                                        setSelectedEmployee(employee);
                                    }}
                                >
                                    <span className={`${classes.avatar} ${colored ? classes.avatarColored : ''}`}>
                                        {employee.photoUrl && !failedPhotoUrls.has(employee.photoUrl) ? (
                                            <img
                                                src={employee.photoUrl}
                                                alt={employee.fullName}
                                                onError={() => handlePhotoError(employee.photoUrl!)}
                                            />
                                        ) : (
                                            initialOf(employee.fullName)
                                        )}
                                    </span>
                                    <span className={classes.rowName}>{employee.fullName}</span>
                                </UnstyledButton>
                            );
                        })}
                        {filteredEmployees.length === 0 && (
                            <p className={classes.empty}>
                                {isLoadingEmployees ? 'טוען עובדים...' : 'לא נמצאו עובדים'}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <PinPadModal
                isOpen={selectedEmployee !== null}
                title="קוד עובד"
                subjectName={selectedEmployee?.fullName ?? ''}
                pinLength={PIN_LENGTH}
                isSubmitting={isIdentifying}
                error={null}
                onClose={closePinModal}
                onSubmit={submitPin}
            />

            {attendanceUiEnabled && onAttendanceAction && attendanceEmployee !== null && attendanceActionType !== null && isAttendanceActionsModalOpen && (
                <div
                    className={classes.attendanceActionsModalOverlay}
                    role="dialog"
                    aria-modal="true"
                    aria-label="בחירת פעולת נוכחות"
                    data-testid="attendance-actions-modal"
                    onClick={() => {
                        if (!isAttendanceActioning) {
                            closeAttendanceFlow();
                        }
                    }}
                >
                    <div className={classes.attendanceActionsModalCard} onClick={(event) => event.stopPropagation()}>
                        <div className={classes.attendanceActionsModalHeader}>
                            <h3 className={classes.attendanceActionsModalTitle}>בחר פעולה</h3>
                            <UnstyledButton
                                type="button"
                                className={classes.attendanceActionsModalClose}
                                onClick={closeAttendanceFlow}
                                disabled={isAttendanceActioning}
                                aria-label="סגור"
                                data-testid="attendance-actions-close"
                            >
                                <FontAwesomeIcon icon={faXmark as IconProp} />
                            </UnstyledButton>
                        </div>
                        <div className={classes.attendanceActionsMeta}>
                            <div className={classes.attendanceActionsEmployeeName}>{attendanceEmployee.fullName}</div>
                            {attendanceActionType === 'clock_out' && attendanceShiftDurationMinutes !== null && (
                                <div className={classes.attendanceActionsShiftTime} data-testid="attendance-open-shift-duration">
                                    זמן במשמרת עכשיו: {formatDurationHmFromMinutes(attendanceShiftDurationMinutes)}
                                </div>
                            )}
                        </div>
                        <div className={classes.attendanceActionsModalButtons}>
                            <UnstyledButton
                                type="button"
                                data-testid="attendance-context-action"
                                className={`${classes.attendanceModalActionButton} ${attendanceActionType === 'clock_out' ? classes.attendanceModalActionButtonOut : classes.attendanceModalActionButtonIn}`}
                                onClick={() => {
                                    onClearError();
                                    setIsAttendanceActionsModalOpen(false);
                                    setAttendancePinOpen(true);
                                }}
                                disabled={isAttendanceActioning || isLoadingEmployees}
                            >
                                <span className={classes.attendanceModalActionIcon} aria-hidden="true">
                                    <FontAwesomeIcon icon={(attendanceActionType === 'clock_out' ? faRightFromBracket : faRightToBracket) as IconProp} />
                                </span>
                                <span className={classes.attendanceModalActionLabel}>
                                    {attendanceActionType === 'clock_out' ? 'יציאה ממשמרת' : 'כניסה למשמרת'}
                                </span>
                            </UnstyledButton>
                        </div>
                    </div>
                </div>
            )}

            <PickListModal
                title="בחירת עובד לנוכחות"
                options={attendanceUiEnabled && isAttendanceActionsModalOpen && attendanceEmployee === null ? sortedEmployees.map((employee) => ({
                    id: employee.id, name: employee.fullName, photoUrl: employee.photoUrl,
                })) : null}
                disabled={isAttendanceActioning || isResolvingAttendanceStatus}
                dataTestId="attendance-employee-pick"
                optionColor="primary"
                variant="employee-grid"
                onPick={(employeeId) => {
                    const found = sortedEmployees.find((employee) => employee.id === employeeId) ?? null;
                    if (!found) {
                        return;
                    }
                    setAttendanceEmployee(found);
                    setIsResolvingAttendanceStatus(true);
                    void Promise.resolve(onResolveAttendanceShiftStatus?.(employeeId))
                        .then((status) => {
                            if (status?.isOpenShift) {
                                setAttendanceActionType('clock_out');
                                setAttendanceShiftDurationMinutes(status.currentDurationMinutes);
                                return;
                            }
                            setAttendanceActionType('clock_in');
                            setAttendanceShiftDurationMinutes(null);
                        })
                        .catch(() => {
                            setAttendanceActionType('clock_in');
                            setAttendanceShiftDurationMinutes(null);
                        })
                        .finally(() => {
                            setIsResolvingAttendanceStatus(false);
                        });
                }}
                onClose={closeAttendanceFlow}
            />

            <PinPadModal
                isOpen={attendanceUiEnabled && attendancePinOpen && attendanceEmployee !== null}
                title={attendanceActionType === 'clock_out' ? 'יציאה ממשמרת' : 'כניסה למשמרת'}
                subjectName={attendanceEmployee?.fullName ?? ''}
                pinLength={PIN_LENGTH}
                isSubmitting={isAttendanceActioning}
                error={null}
                onClose={closeAttendanceFlow}
                onSubmit={submitAttendancePin}
            />
        </div>
    );
};
